import { useMemo } from 'react';

import { useTranslation } from 'react-i18next';

import type {
  OptionGroup,
  ControllableOption,
  ControllableOptionGroup,
  Option,
} from '../types';
import { useArrayStateController } from './useArrayStateController';
import { useDynamicOptionGroups } from './useDynamicOptionGroups';

interface UseOptionsFilterControllerProps<O extends Option = Option> {
  optionGroups: OptionGroup<O>[];
  value?: string[];
  defaultValue?: string[];
  onChange?: (value: string[]) => void;
  isCreatable?: boolean;
}

interface UseOptionsFilterControllerReturn {
  addDynamicOption: (value: string) => string | boolean | undefined;
  reset?: VoidFunction;
  controllableOptionGroups: ControllableOptionGroup[];
}

export const useOptionsFilterController = <O extends Option = Option>({
  optionGroups,
  value,
  defaultValue = [],
  onChange,
}: UseOptionsFilterControllerProps<O>): UseOptionsFilterControllerReturn => {
  const { t } = useTranslation();

  const arrayField = useArrayStateController({
    value,
    defaultValue,
    onChange,
  });

  const { dynamicOptionGroups, addDynamicOption } = useDynamicOptionGroups({ optionGroups });

  const controllableOptionGroups: ControllableOptionGroup[] = useMemo(() => {
    if (!dynamicOptionGroups?.length) {
      return [];
    }

    return dynamicOptionGroups.map((optionGroup) => ({
      ...optionGroup,
      options: optionGroup.options.map((option) => {
        const controlledOption: ControllableOption = {
          ...option,
          isSelected: arrayField.isSelected(option.value),
          toggle: () => arrayField.toggleOption(option.value),
        };

        const optionWithRemove = option as O & { remove?: VoidFunction };

        if (optionWithRemove.remove) {
          controlledOption.remove = () => {
            arrayField.removeOption(option.value);
            optionWithRemove.remove?.();
          };
        }

        return controlledOption;
      }),
    }));
  }, [arrayField, dynamicOptionGroups]);

  const handleAddDynamicOption = (optionValue: string) => {
    const trimmed = optionValue.trim();
    if (!trimmed) return undefined;

    const newOption = {
      label: trimmed,
      value: trimmed,
    } as O;

    if (addDynamicOption(newOption)) {
      arrayField.toggleOption(trimmed);
      return true;
    }

    return t('значение уже существует');
  };

  return {
    addDynamicOption: handleAddDynamicOption,
    reset: arrayField.reset,
    controllableOptionGroups,
  };
};
